import { useI18n } from '@/i18n';
import { router } from '@/router';
import { startErrorReporting } from '@/sentry';
import { registerQueryClient, useContextStore } from '@/shell/context';
import { applyTheme, readTheme } from '@/shell/theme';
import { TooltipProvider } from '@/ui';
import { QueryClient, QueryClientProvider, hashKey } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import './tokens.css';
import './styles.css';

// Locale before first paint: no flicker.
document.documentElement.lang = useI18n.getState().locale;
// Theme too, for the same reason: applied after mount it is a flash of the other palette.
applyTheme(readTheme());

// every key is hashed with the context generation (TF-COMPANY-SWITCH): a query
// started in one company can never be read, or repainted, in the next
const qc = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      queryKeyHashFn: (key) => hashKey([useContextStore.getState().gen, ...key]),
    },
  },
});
registerQueryClient(qc);

// Client errors go to Sentry when the container names a DSN (src/sentry.ts, 2026-10-19).
startErrorReporting();
window.addEventListener('error', (e) => console.warn('[timefly] uncaught', e.message));

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <TooltipProvider>
        <RouterProvider router={router} />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
